import styles from './PasswordField.module.scss';
import Box from '@components/Box';
import ProgressBar from '@components/ProgressBar';
import TextInputContainer from '@containers/TextInputContainer';
import TooltipIcon from '@assets/icons/tooltip.svg';
import zxcvbn from 'zxcvbn';
import { Column, Row } from '@components/Grid';
import { FieldValues, Path } from 'react-hook-form';
import { IPasswordFieldProps } from './types';
import { Text, TextColor, TextSize, TextWeight } from '@components/Text';
import { Tooltip } from '@components/new/Tooltip';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const PasswordField = <T extends FieldValues>(props: IPasswordFieldProps<T>): React.ReactElement => {
  const {
    calledFrom,
    control,
    hasError,
    name,
    password,
    pb,
    showPasswordStrength = true
  } = props;

  const { t } = useTranslation();

  const description: string[] = [
    t('signUp.email.passwordDescription'),
    t('signUp.email.passwordDescription2'),
    t('signUp.email.passwordDescription3'),
    t('signUp.email.passwordDescription4'),
    t('signUp.email.passwordDescription5')
  ];
  const passScore: number = useMemo(() => zxcvbn(password).score, [password]);

  return (
      <>
        <Row>
          <Column phone={12}>
            <Box pb={showPasswordStrength ? 2 : 0}>
              <TextInputContainer<T>
                  control={control}
                  hasError={hasError ?? false}
                  name={(name ?? 'password') as Path<T>}
                  type="password"
                  title={calledFrom ? t(`${calledFrom}.${name}`) : t('signUp.email.inputPass')}
              />
            </Box>
          </Column>
        </Row>
        {showPasswordStrength && (
            <Row>
              <Column phone={12}>
                <Box pb={pb ?? 0}>
                  <ProgressBar
                      colors={[
                        { value: 25, main: '#ee6373' },
                        { value: 50, main: '#f2af4c' },
                        { value: 75, main: '#3ecc92' },
                        { value: 100, main: '#4ca36a' }
                      ]}
                      column
                      label={(
                          <>
                            <Text size={TextSize.S}>
                              {t('signUp.email.passStrengthTitle')}
                              {': '}
                              <b>{t('signUp.email.passStrengthValue', { context: passScore })}</b>
                            </Text>
                            <Tooltip
                                action={
                                  <img
                                      alt="Tooltip"
                                      src={TooltipIcon}
                                  />
                                }
                                text={(
                                    <div className={styles.tooltip}>
                                      {description.map((item: string, index: number) => (
                                              <Text
                                                  color={TextColor.WHITE}
                                                  key={index}
                                                  size={TextSize.XS}
                                                  weight={index ? TextWeight.NORMAL : TextWeight.BOLD}
                                              >
                                                {index ? '- ' + item : item}
                                              </Text>
                                          )
                                      )}
                                    </div>
                                )}
                                position="top"
                            />
                          </>
                      )}
                      progress={25 * +passScore}
                      rounded
                  />
                </Box>
              </Column>
            </Row>
        )}
      </>
  );
};

export default PasswordField;
